import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/sections/Footer';
import { SEO } from '@/components/SEO';

const CookiePolicy = () => {
  return (
    <main className="min-h-screen bg-background">
      <SEO
        title="Cookie Policy - Brace e Sapori Store | Informativa sui Cookie"
        description="Cookie Policy di Brace e Sapori Store. Scopri quali cookie utilizziamo, come gestirli e le tue opzioni. Utilizziamo solo cookie tecnici necessari per il funzionamento del sito."
        keywords="cookie policy store, informativa cookie e-commerce, gestione cookie, cookie tecnici, privacy cookie"
        canonical="https://store.braceesapori.it/cookie-policy"
      />
      <Header />
      <section className="py-16 px-6 md:py-24">
        <div className="mx-auto max-w-4xl">
          <div className="mb-8">
            <h1 className="mb-4 text-4xl font-semibold text-foreground md:text-5xl">
              Cookie Policy
            </h1>
            <p className="text-sm text-muted-foreground">
              Ultimo aggiornamento: {new Date().toLocaleDateString('it-IT', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          <div className="prose prose-slate max-w-none dark:prose-invert">
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">1. Cosa sono i Cookie</h2>
            <p className="text-muted-foreground mb-6">
              I cookie sono piccoli file di testo che vengono memorizzati sul dispositivo dell'utente (computer, 
              tablet, smartphone) quando si visita un sito web. I cookie permettono al sito di ricordare le 
              azioni e le preferenze dell'utente per un determinato periodo di tempo, evitando così di doverle 
              reinserire ogni volta che si torna sul sito o si naviga da una pagina all'altra.
            </p>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">2. Tipi di Cookie Utilizzati</h2>
            <p className="text-muted-foreground mb-4">
              Il presente sito web utilizza i seguenti tipi di cookie:
            </p>

            <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">2.1 Cookie Tecnici (Necessari)</h3>
            <p className="text-muted-foreground mb-4">
              I cookie tecnici sono essenziali per il corretto funzionamento del sito e non possono essere 
              disabilitati. Questi cookie:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
              <li>Permettono la navigazione e l'utilizzo delle funzionalità base del sito</li>
              <li>Memorizzano le preferenze dell'utente (ad esempio, il contenuto del carrello, lo stato di autenticazione)</li>
              <li>Garantiscono la sicurezza durante la navigazione</li>
              <li>Consentono il corretto funzionamento delle funzionalità interattive</li>
            </ul>

            <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">2.2 Cookie di Sessione</h3>
            <p className="text-muted-foreground mb-4">
              Il sito utilizza cookie di sessione che vengono eliminati automaticamente alla chiusura del browser. 
              Questi cookie sono necessari per mantenere lo stato della sessione durante la navigazione.
            </p>

            <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">2.3 Cookie di Analytics e Marketing</h3>
            <p className="text-muted-foreground mb-4">
              Il sito utilizza cookie di terze parti per finalità di analisi statistica e marketing digitale. 
              Questi cookie richiedono il consenso dell'utente:
            </p>
            
            <div className="bg-card border border-border rounded-lg p-6 mb-4">
              <h4 className="font-semibold text-foreground mb-2">Google Analytics e Google Tag Manager</h4>
              <p className="text-muted-foreground mb-2">
                Utilizziamo cookie di Google Analytics per analizzare il comportamento degli utenti sul sito. 
                Questi cookie raccolgono informazioni in forma aggregata e anonima.
              </p>
              <p className="text-muted-foreground mb-2">
                <strong className="text-foreground">Cookie utilizzati:</strong> _ga, _ga_*, _gid, _gat
              </p>
              <p className="text-muted-foreground mb-2">
                <strong className="text-foreground">Durata:</strong> fino a 2 anni (cookie persistenti)
              </p>
              <p className="text-muted-foreground">
                <strong className="text-foreground">Finalità:</strong> analisi statistica del traffico, miglioramento del sito
              </p>
              <p className="text-muted-foreground mt-2">
                <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Opt-out Google Analytics
                </a>
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 mb-6">
              <h4 className="font-semibold text-foreground mb-2">Meta Pixel (Facebook Pixel)</h4>
              <p className="text-muted-foreground mb-2">
                Utilizziamo cookie di Meta Pixel per tracciare le conversioni e ottimizzare le campagne pubblicitarie 
                su Facebook e Instagram.
              </p>
              <p className="text-muted-foreground mb-2">
                <strong className="text-foreground">Cookie utilizzati:</strong> _fbp, fr, sb
              </p>
              <p className="text-muted-foreground mb-2">
                <strong className="text-foreground">Durata:</strong> fino a 90 giorni (cookie persistenti)
              </p>
              <p className="text-muted-foreground">
                <strong className="text-foreground">Finalità:</strong> misurazione performance campagne, remarketing, creazione audience
              </p>
              <p className="text-muted-foreground mt-2">
                <a href="https://www.facebook.com/settings?tab=ads" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Gestisci le tue preferenze pubblicitarie su Facebook
                </a>
              </p>
            </div>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">3. Cookie di Profilazione</h2>
            <p className="text-muted-foreground mb-4">
              Il presente sito web utilizza cookie di profilazione per finalità di marketing e analisi statistica. 
              Questi cookie richiedono il consenso esplicito dell'utente.
            </p>
            <p className="text-muted-foreground mb-6">
              I cookie di profilazione vengono utilizzati per:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
              <li>Creare profili degli utenti basati sulle loro preferenze di navigazione</li>
              <li>Tracciare le conversioni e-commerce (visualizzazioni prodotto, aggiunte al carrello, acquisti)</li>
              <li>Ottimizzare le campagne pubblicitarie su Google e Meta</li>
              <li>Creare audience personalizzate per il remarketing</li>
              <li>Analizzare il comportamento degli utenti per migliorare l'esperienza sul sito</li>
            </ul>
            <p className="text-muted-foreground mb-6">
              Puoi gestire o revocare il consenso ai cookie di profilazione in qualsiasi momento attraverso le impostazioni 
              del tuo browser o utilizzando gli strumenti di opt-out forniti da Google e Meta (vedi sezione 2.3).
            </p>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">4. Tecnologie di Tracciamento</h2>
            <p className="text-muted-foreground mb-4">
              Il presente sito web impiega le seguenti tecnologie di tracciamento per finalità di marketing e analisi:
            </p>
            <div className="bg-card border border-border rounded-lg p-6 mb-6">
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong className="text-foreground">Google Tag Manager (gtag.js):</strong> sistema di gestione tag per integrare Google Analytics e altri servizi</li>
                <li><strong className="text-foreground">Google Analytics:</strong> servizio di analisi web che utilizza cookie e script JavaScript</li>
                <li><strong className="text-foreground">Meta Pixel (Facebook Pixel):</strong> pixel di tracciamento per misurare conversioni e ottimizzare campagne pubblicitarie</li>
                <li><strong className="text-foreground">Local Storage e Session Storage:</strong> utilizzati per memorizzare preferenze utente e dati del carrello</li>
              </ul>
            </div>
            <p className="text-muted-foreground mb-6">
              Queste tecnologie raccolgono dati in forma aggregata e anonima per finalità di analisi statistica e marketing. 
              I dati vengono utilizzati per migliorare l'esperienza utente e ottimizzare le campagne pubblicitarie.
            </p>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">5. Gestione dei Cookie</h2>
            <p className="text-muted-foreground mb-4">
              Puoi gestire le tue preferenze sui cookie in diversi modi:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-4 space-y-2">
              <li><strong className="text-foreground">Impostazioni del browser:</strong> puoi disabilitare o eliminare i cookie attraverso le impostazioni del tuo browser</li>
              <li><strong className="text-foreground">Opt-out Google Analytics:</strong> puoi installare il componente aggiuntivo del browser per la disattivazione di Google Analytics</li>
              <li><strong className="text-foreground">Preferenze Facebook:</strong> puoi gestire le tue preferenze pubblicitarie direttamente su Facebook</li>
            </ul>
            <p className="text-muted-foreground mb-4">
              <strong className="text-foreground">Nota importante:</strong> La disabilitazione dei cookie tecnici potrebbe compromettere 
              il corretto funzionamento del sito e impedire l'accesso ad alcune funzionalità. La disabilitazione dei cookie di profilazione 
              non influisce sul funzionamento base del sito.
            </p>

            <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">5.1 Come Disabilitare i Cookie</h3>
            <p className="text-muted-foreground mb-4">
              È possibile disabilitare i cookie modificando le impostazioni del browser. Di seguito i link alle 
              guide per i browser più comuni:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
              <li>
                <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Google Chrome
                </a>
              </li>
              <li>
                <a href="https://support.mozilla.org/it/kb/Attivare%20e%20disattivare%20i%20cookie" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Mozilla Firefox
                </a>
              </li>
              <li>
                <a href="https://support.apple.com/it-it/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Safari
                </a>
              </li>
              <li>
                <a href="https://support.microsoft.com/it-it/microsoft-edge/eliminare-i-cookie-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Microsoft Edge
                </a>
              </li>
            </ul>
            <p className="text-muted-foreground mb-6">
              <strong className="text-foreground">Nota importante:</strong> La disabilitazione dei cookie tecnici 
              potrebbe compromettere il corretto funzionamento del sito e impedire l'accesso ad alcune funzionalità.
            </p>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">6. Cookie di Terze Parti</h2>
            <p className="text-muted-foreground mb-4">
              Il presente sito web utilizza cookie di terze parti per finalità di analisi e marketing:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
              <li><strong className="text-foreground">Google:</strong> Google Analytics e Google Tag Manager utilizzano cookie per analizzare il traffico</li>
              <li><strong className="text-foreground">Meta (Facebook):</strong> Meta Pixel utilizza cookie per tracciare conversioni e ottimizzare campagne pubblicitarie</li>
            </ul>
            <p className="text-muted-foreground mb-6">
              Questi servizi di terze parti possono installare cookie sul dispositivo dell'utente. Per maggiori informazioni 
              su come queste terze parti utilizzano i cookie, consulta le rispettive privacy policy:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
              <li><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Privacy Policy di Google</a></li>
              <li><a href="https://www.facebook.com/privacy/explanation" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Privacy Policy di Meta</a></li>
            </ul>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">7. Durata dei Cookie</h2>
            <p className="text-muted-foreground mb-4">
              I cookie utilizzati dal sito hanno durate diverse:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
              <li><strong className="text-foreground">Cookie di sessione:</strong> vengono eliminati automaticamente alla chiusura del browser</li>
              <li><strong className="text-foreground">Cookie tecnici persistenti:</strong> rimangono memorizzati fino alla scadenza o alla cancellazione manuale</li>
              <li><strong className="text-foreground">Cookie Google Analytics:</strong> durata fino a 2 anni</li>
              <li><strong className="text-foreground">Cookie Meta Pixel:</strong> durata fino a 90 giorni</li>
            </ul>
            <p className="text-muted-foreground mb-6">
              Puoi eliminare i cookie in qualsiasi momento attraverso le impostazioni del tuo browser.
            </p>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">8. Aggiornamenti della Cookie Policy</h2>
            <p className="text-muted-foreground mb-6">
              La presente Cookie Policy può essere aggiornata periodicamente per riflettere eventuali modifiche nelle 
              tecnologie utilizzate o nella normativa applicabile. Si consiglia di consultare periodicamente questa 
              pagina per essere informati su eventuali aggiornamenti.
            </p>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">9. Contatti</h2>
            <p className="text-muted-foreground mb-4">
              Per qualsiasi domanda o richiesta relativa all'utilizzo dei cookie, è possibile contattare il titolare:
            </p>
            <div className="bg-card border border-border rounded-lg p-6 mb-6">
              <p className="text-muted-foreground mb-2">
                <strong className="text-foreground">Av Food Srl</strong>
              </p>
              <p className="text-muted-foreground mb-2">
                Via P. B. Moscatelli, 8<br />
                74027 San Giorgio Ionico (TA)
              </p>
              <p className="text-muted-foreground mb-2">
                <strong className="text-foreground">Email:</strong>{' '}
                <a href="mailto:braceesapori@yahoo.com" className="text-primary hover:underline">
                  braceesapori@yahoo.com
                </a>
              </p>
              <p className="text-muted-foreground">
                <strong className="text-foreground">Telefono:</strong>{' '}
                <a href="tel:+393472916493" className="text-primary hover:underline">
                  347 291 6493
                </a>
              </p>
            </div>

            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">10. Riferimenti Normativi</h2>
            <p className="text-muted-foreground mb-6">
              La presente Cookie Policy è redatta in conformità con:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
              <li>Regolamento (UE) 2016/679 (GDPR) - Regolamento Generale sulla Protezione dei Dati</li>
              <li>Direttiva 2002/58/CE (ePrivacy Directive) come modificata dalla Direttiva 2009/136/CE</li>
              <li>D.Lgs. 196/2003 (Codice Privacy) come modificato dal D.Lgs. 101/2018</li>
              <li>Provvedimento del Garante per la protezione dei dati personali del 8 maggio 2014 "Individuazione 
              delle modalità semplificate per l'informativa e l'acquisizione del consenso per l'uso dei cookie"</li>
            </ul>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
};

export default CookiePolicy;

